<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import Bar from '@/components/common/dashboard/chart/Bar.vue'
import { getThisYear, numberFormatter } from '@/js/customChart'
import { getCategoriesOneValues, getCategoriesTwoValues, getCategoriesThreeValues } from '@/api/dashboard'
import { useNavigatorStore } from '@/store/navigator'
const navigatorStore = useNavigatorStore()

import { useUserStore } from '@/store/user'
const userStore = useUserStore()

// props
const props = defineProps({
  chartType: String,
  employeeId: String,
  legend: String,
  width: Number,
  height: Number,
  title: String,
  data: Object,
  fiscalYear:String
})

// data
const fetchedData = ref()
const totalValue = ref({
  revsumdecimal: 0,
  gmsumdecimal: 0
})

// computed
const chartOptions = computed(() => {
  const axis = props.chartType === 'vertical' ? 'x' : 'y'
  const scales = props.chartType !== 'vertical' ? 'x' : 'y'
  const CHART_OPTIONS = {
    // responsive: false,
    aspectRatio: navigatorStore.isMobile ? 1.2 : 2,
    layout: {
      padding: {
        top: 50,
      }
    },
    indexAxis: axis,
    devicePixelRatio: 1,
    maintainAspectRatio: navigatorStore.isMobile,
    scales: {
      [scales]: {
        beginAtZero: true,
        ticks: {
          callback: function (value, index, ticks) {
            return `${value}억`;
          }
        }
      }
    },
    plugins: {
      title: {
        display: true,
        text: `Total Rev.(GM) FCST : ${numberFormatter(totalValue.value.revsumdecimal) || 0}억(${numberFormatter(totalValue.value.gmsumdecimal) || 0}억)`,
        position: 'bottom',
        color: '#000000',
        font: {
          size: navigatorStore.isMobile ? 12 : 15,
          family: 'suit-m',
          weight: 'normal'
        }
      },
      legend: {
        display: false,
        position: "bottom",
        align: 'start',
        title: {
          // display: true,
          text: 'Legend Title',
        }
      },
      tooltip: {
        usePointStyle: true,
        callbacks: {
          labelPointStyle() {
            return {
              pointStyle: false,
            };
          },
          title([data]) {
            return `Total Rev. FCST ${fetchedData.value.additionalData.rawData[data.dataIndex]}억원`
          },
          label(data) {
            return fetchedData.value.additionalData.tooltip[data.label]
          }
        }
      }
    }
  }
  return CHART_OPTIONS
})

// watch
watch([() => props.employeeId, () => props.legend, ()=>props.fiscalYear], async newValue => {
  const [employeeId, legend,fiscalYear] = newValue
  try {
    await getMonthlyRevGmFCST(legend, employeeId,fiscalYear)
  } catch (err) {
    console.error(err)
  }
})

// lifecycle
onMounted(async () => {
  const employeeId = userStore.isAdmin ? '' : userStore.user.employeeId
  try {
    await getMonthlyRevGmFCST(props.legend, props.employeeId || employeeId,props.fiscalYear)
  } catch (err) {
    console.error(err)
  }
})

function getMappedData(result) {
  const axisX = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const groupByResult = Object.groupBy(result.rtn, ({ cal }) => cal)
  const [{ gmsumdecimal, revsumdecimal }] = groupByResult['']
  function getSumValues(key) {
    return Object.entries(groupByResult)
      .filter(([key]) => key)
      .map(([, value]) => {
        return value.find(item => item.sortord === '1')[`${key}sumdecimal`]
      })
  }
  return {
    axisX,
    groupByResult,
    totalValues: {
      revsumdecimal,
      gmsumdecimal,
    },
    sumRevValues: getSumValues('rev'),
    sumGmValues: getSumValues('gm')
  }
}

function getChartData({ axisX, groupByResult, totalValues, sumRevValues: data, sumGmValues: dataPer }) {
  totalValue.value = totalValues
  return {
    data: {
      labels: [...axisX],
      datasets: [
        {
          data: data
        }
      ]
    },
    additionalData: {
      rawData: data,
      dataPer,
      tooltip: {
        ...Object.fromEntries(Object.entries(groupByResult).map(([key, value]) => {
          const mappedValue = value.map((item, index) => {
            return `${index + 1}. ${item.itemname} ${numberFormatter(item.revdecimal)}억원`
          })
          return [key, mappedValue]
        }))
      }
    }
  }
}

async function getMonthlyRevGmFCST(legend, employeeId,fiscalYear) {
  const APIS = {
    enduser: getCategoriesOneValues,
    goods: getCategoriesTwoValues,
    purchaser: getCategoriesThreeValues,
  }
  try {
    if(legend){
      const result = await APIS[legend]({
        fs_year: fiscalYear,
        employee_id: employeeId
      })
      fetchedData.value = getChartData(getMappedData(result))
    }
   
    
  } catch (err) {
    console.error(err)
  }
}

</script>

<template>
  <Bar :width="width" :height="height" :data="fetchedData?.data" :additionalData="fetchedData?.additionalData"
    :title="title" :options="chartOptions" :type ="props.chartType"/>
</template>

<style scoped>

</style>